/**
 * addon-flow.js
 *
 * Client controller for the multi-step "Essential add-ons" funnel
 * (sections/addon-flow.liquid).
 *
 * Phases:
 *  - P2: wizard shell + selection state + client summary preview.
 *  - P3 (this): progressive cart writes. The machine is ensured in the cart on
 *    entry; each "Add and Continue" POSTs the current step's selected items to
 *    /cart/add.js, re-fetches /cart.js, and re-renders the summary from real
 *    cart state. Rebuy's Smart Cart drawer is suppressed on this page so our
 *    own summary panel is the single cart UI (the one sanctioned exception to
 *    the theme's "let Rebuy own the drawer" rule — see project memory).
 *
 * Summary model: real cart lines (machine + already-committed steps, from
 * /cart.js) PLUS a live preview of selected cards in steps not yet committed.
 * Committing a step moves its items from preview into the real cart, so there
 * is never a double count.
 *
 * JS pattern: IIFE, no globals, re-inits on `shopify:section:load`.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-addon-flow]';
  var CART_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function formatMoney(cents) {
    var n = Number(cents) / 100;
    if (Number.isNaN(n)) return '';
    var rounded = Math.round(n * 100) / 100;
    var hasCents = rounded % 1 !== 0;
    return '$' + rounded.toLocaleString('en-US', {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: 2
    });
  }

  function parseJSON(el, fallback) {
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return fallback;
    }
  }

  /* ── Cart API ────────────────────────────────────────────────────────── */

  // Fetch JSON, retrying a couple of times on 429 (the storefront /cart endpoints
  // throttle per session/IP; a transient 429 shouldn't break the flow). Respects
  // Retry-After when present; capped so we never stall the UI for long.
  function fetchCartJSON(url, options, retriesLeft) {
    if (retriesLeft == null) retriesLeft = 2;
    return fetch(url, options).then(function (r) {
      if (r.status === 429 && retriesLeft > 0) {
        var retryAfter = parseFloat(r.headers.get('Retry-After'));
        var delay = Math.min((retryAfter > 0 ? retryAfter : 1) * 1000, 3000);
        return new Promise(function (resolve) { setTimeout(resolve, delay); })
          .then(function () { return fetchCartJSON(url, options, retriesLeft - 1); });
      }
      if (!r.ok) throw new Error(url + ' failed: ' + r.status);
      return r.json();
    });
  }

  function cartAdd(items) {
    return fetchCartJSON(CART_ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    });
  }

  function cartGet() {
    return fetchCartJSON(CART_ROOT + 'cart.js', { headers: { Accept: 'application/json' } });
  }

  /* ── A/B tracking (Phase 1 §8 dl_add_to_cart, §9 cart attributes) ─────────
   * SCAFFOLD — disabled until the Convert experience is live and each payload
   * is verified in the Elevar debugger. Flip AB_TRACKING.enabled to true only
   * after: (1) the exposure snippet (snippets/addon-flow-ab-tracking.liquid) is
   * wired with the real Convert experience ID, and (2) the dl_add_to_cart shape
   * below is confirmed against Elevar's schema (it rejects unknown/mistyped
   * fields).
   *
   * Data-flow note: the experiment arm (CONV-<exp>-<var>) is only known on the
   * PDP, where Convert buckets the visitor — NOT here on the funnel template
   * (the experiment's URL targeting excludes it). So the arm attribute
   * `ab_addon_flow` is written by the exposure snippet on the PDP. This file
   * only manages `addon_flow_in_progress` (funnel lifecycle, for the Phase 4
   * abandoned-cart suppression) and the per-item dl_add_to_cart pushes for the
   * AJAX adds Elevar's Shopify listener can't auto-detect. */
  var AB_TRACKING = { enabled: false };

  function cartUpdateAttributes(attributes) {
    return fetchCartJSON(CART_ROOT + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    });
  }

  // Mark / clear the "funnel in progress" flag (Phase 4). Set true when the
  // funnel seeds the machine, cleared when the shopper reaches checkout, so
  // abandoned-cart recovery can suppress carts that only exist because the
  // funnel auto-seeded the machine. Best-effort; no-op until tracking is on.
  function setFlowInProgress(inProgress) {
    if (!AB_TRACKING.enabled) return Promise.resolve();
    return cartUpdateAttributes({ addon_flow_in_progress: inProgress ? 'true' : '' })
      .catch(function () { /* attribute writes are best-effort */ });
  }

  // Push dl_add_to_cart to Elevar for lines newly added/increased between two
  // /cart.js snapshots. Reads cart-line fields (title/price/sku/…) so we don't
  // have to thread product data through the card DOM. Elevar's Shopify listener
  // catches native form adds but NOT our AJAX adds — without this the variation
  // looks like it collapses on add-to-cart (a pure tracking artifact).
  // TODO(convert): verify this payload in the Elevar debugger before enabling.
  function pushAddToCartDelta(prevCart, nextCart) {
    if (!AB_TRACKING.enabled || !nextCart || !window.ElevarDataLayer) return;
    var before = {};
    ((prevCart && prevCart.items) || []).forEach(function (i) { before[i.key] = i.quantity; });
    var added = [];
    (nextCart.items || []).forEach(function (i) {
      var delta = i.quantity - (before[i.key] || 0);
      if (delta > 0) added.push({ line: i, quantity: delta });
    });
    if (!added.length) return;
    window.ElevarDataLayer.push({
      event: 'dl_add_to_cart',
      ecommerce: {
        currencyCode: nextCart.currency || 'USD',
        add: {
          actionField: { list: window.location.pathname },
          products: added.map(function (a) {
            var i = a.line;
            return {
              id: i.sku || String(i.variant_id),
              name: i.product_title,
              price: (i.price / 100).toFixed(2),
              quantity: String(a.quantity),
              product_id: String(i.product_id),
              variant_id: String(i.variant_id),
              variant: i.variant_title || '',
              brand: i.vendor || '',
              category: i.product_type || ''
            };
          })
        }
      }
    });
  }

  /* ── Rebuy suppression ───────────────────────────────────────────────── */

  function suppressRebuy() {
    if (!window.Rebuy || !window.Rebuy.SmartCart) return false;
    var sc = window.Rebuy.SmartCart;
    try { if (sc.unbindEvents) sc.unbindEvents(); } catch { /* noop */ }
    try { if (sc.hide) sc.hide(); } catch { /* noop */ }
    return true;
  }

  // Rebuy may load after us; keep trying briefly until SmartCart is present.
  function trySuppressRebuy(attempts) {
    if (suppressRebuy() || attempts <= 0) return;
    setTimeout(function () {
      trySuppressRebuy(attempts - 1);
    }, 300);
  }

  /* ── Controller ──────────────────────────────────────────────────────── */

  function AddonFlow(root) {
    this.root = root;
    this.steps = Array.prototype.slice.call(root.querySelectorAll('[data-addon-flow-step]'));
    this.markers = Array.prototype.slice.call(root.querySelectorAll('[data-step-marker]'));
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-addon-card]'));

    this.summaryEl = root.querySelector('[data-addon-flow-summary]');
    this.summaryToggle = root.querySelector('[data-addon-summary-toggle]');
    this.itemsEl = root.querySelector('[data-addon-summary-items]');
    this.subtotalEl = root.querySelector('[data-addon-summary-subtotal]');
    this.countEl = root.querySelector('[data-addon-summary-count]');
    this.countNounEl = root.querySelector('[data-addon-summary-count-noun]');
    this.continueBtn = root.querySelector('[data-addon-flow-continue]');
    this.skipBtn = root.querySelector('[data-addon-flow-skip]');
    this.checkoutLink = root.querySelector('[data-addon-flow-checkout]');
    this.skipModal = root.querySelector('[data-addon-flow-skip-modal]');

    var urlVariant = new URLSearchParams(window.location.search).get('variant');
    this.machineVariantId = urlVariant || root.getAttribute('data-machine-variant-id') || '';

    this.current = 0;
    this.committed = {}; // step index -> true once the user proceeds through it
    this.cart = { items: [], total_price: 0, item_count: 0 };
    this.machineSeeded = false; // the machine is added to the cart on the first advance, not on entry
    this.busy = false;

    this.init();
  }

  AddonFlow.prototype.init = function () {
    if (!this.steps.length) return;

    trySuppressRebuy(20);
    this.bindNavigation();
    this.cards.forEach(this.bindCard.bind(this));
    this.goTo(0);

    // The machine is in the cart summary from the start (it's the base product);
    // only the ADD-ONS build up as the customer advances through the steps.
    var self = this;
    this.ensureMachine()
      .then(function (cart) {
        self.cart = cart;
        self.machineSeeded = true;
        setFlowInProgress(true); // Phase 4: mark the funnel-seeded cart (no-op until tracking on)
        self.renderSummary();
      })
      .catch(function (e) {
        // On the *.myshopify.com preview domain /cart.js redirects cross-origin
        // and fails CORS; the summary still renders from local state, and the
        // machine seeds on the first advance as a fallback.
        console.warn('[addon-flow] cart unavailable:', e);
        self.renderSummary();
      });
  };

  // Ensure the machine variant is in the cart on entry (added once if absent).
  AddonFlow.prototype.ensureMachine = function () {
    var vid = this.machineVariantId;
    return cartGet().then(function (cart) {
      var present = vid && cart.items.some(function (i) {
        return String(i.variant_id) === String(vid);
      });
      if (!vid || present) return cart;
      return cartAdd([{ id: vid, quantity: 1 }]).then(function () {
        suppressRebuy();
        return cartGet().then(function (next) {
          pushAddToCartDelta(cart, next); // the machine was just added
          return next;
        });
      });
    });
  };

  // Machine fallback for forward actions if entry-seeding failed (e.g. CORS on
  // the preview domain). Returns [] once the machine is in the cart.
  AddonFlow.prototype.machineSeedItems = function () {
    if (this.machineSeeded || !this.machineVariantId) return [];
    return [{ id: parseInt(this.machineVariantId, 10), quantity: 1 }];
  };

  /* ── Navigation ──────────────────────────────────────────────────────── */

  AddonFlow.prototype.bindNavigation = function () {
    var self = this;

    // "Add and go to {next}" — commit this step's items (+ seed the machine on
    // the first advance), then move on.
    if (this.continueBtn) {
      this.continueBtn.addEventListener('click', function () {
        self.advance(true);
      });
    }
    // "No thanks" — skip this step's add-ons and move to the next step (still
    // seeds the machine on the first advance).
    if (this.skipBtn) {
      this.skipBtn.addEventListener('click', function () {
        self.advance(false);
      });
    }
    // "Skip to Checkout" — abandon the remaining add-ons and check out. If any
    // essential step hasn't been addressed, confirm first.
    if (this.checkoutLink) {
      this.checkoutLink.addEventListener('click', function () {
        self.skipToCheckout();
      });
    }

    if (this.skipModal) {
      // "Back to Add-Ons" (+ the close X) just dismiss the dialog.
      Array.prototype.slice.call(this.skipModal.querySelectorAll('[data-addon-skip-cancel]'))
        .forEach(function (btn) {
          btn.addEventListener('click', function () { self.skipModal.close(); });
        });
      // Confirm → seed the machine if needed and go to checkout.
      var confirmBtn = this.skipModal.querySelector('[data-addon-skip-confirm]');
      if (confirmBtn) {
        confirmBtn.addEventListener('click', function () {
          self.skipModal.close();
          self.goToCheckout();
        });
      }
      // Click on the backdrop closes.
      this.skipModal.addEventListener('click', function (e) {
        if (e.target === self.skipModal) self.skipModal.close();
      });
    }

    this.root.addEventListener('click', function (e) {
      var back = e.target.closest('[data-addon-flow-back]');
      if (back && self.root.contains(back)) self.goTo(self.current - 1);
    });

    // Mobile: expand/collapse the sticky summary sheet.
    if (this.summaryToggle && this.summaryEl) {
      this.summaryToggle.addEventListener('click', function () {
        var expanded = self.summaryEl.classList.toggle('is-expanded');
        self.summaryToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    }
  };

  AddonFlow.prototype.goTo = function (index) {
    if (index < 0 || index >= this.steps.length) return;
    this.current = index;

    this.steps.forEach(function (step, i) {
      var active = i === index;
      step.classList.toggle('is-active', active);
      if (active) step.removeAttribute('hidden');
      else step.setAttribute('hidden', '');
    });

    this.markers.forEach(function (marker, i) {
      marker.classList.toggle('is-active', i === index);
      marker.classList.toggle('is-done', i < index);
      if (i === index) marker.setAttribute('aria-current', 'step');
      else marker.removeAttribute('aria-current');
    });

    this.updateActions();
    this.renderSummary();
  };

  AddonFlow.prototype.updateActions = function () {
    if (!this.continueBtn) return;
    var label = this.continueBtn.querySelector('span') || this.continueBtn;
    if (this.current >= this.steps.length - 1) {
      label.textContent = 'Add and Checkout';
    } else {
      var next = this.steps[this.current + 1];
      var nextTitle = next ? (next.getAttribute('aria-label') || '') : '';
      label.textContent = nextTitle ? 'Add and go to ' + nextTitle : 'Add and Continue';
    }
  };

  // Forward step action. includeStepItems=true → "Add and go to X" (commit this
  // step's selections); false → "No thanks" (skip). Both seed the machine on the
  // first advance so the cart builds up as the customer moves through the flow.
  AddonFlow.prototype.advance = function (includeStepItems) {
    if (this.busy) return;
    var self = this;
    var items = this.machineSeedItems();
    // #5 idempotency: commit a step's items at most once. Going Back to an
    // already-committed step and clicking continue again must NOT re-add its
    // add-ons (the machine is separately deduped by ensureMachine). Editing a
    // committed step's selection is out of scope here — that would need
    // /cart/change.js to *set* quantities rather than /cart/add.js to increment.
    var prevCart = this.cart;
    if (includeStepItems && !this.committed[this.current]) {
      items = items.concat(this.collectStepItems(this.current));
      this.committed[this.current] = true;
    }

    if (!items.length) {
      this.advanceOrFinish();
      return;
    }

    this.setBusy(true);
    cartAdd(items)
      .then(function () {
        self.machineSeeded = true;
        suppressRebuy();
        return cartGet();
      })
      .then(function (cart) {
        pushAddToCartDelta(prevCart, cart); // §8: the step's add-ons just added
        self.cart = cart;
        self.setBusy(false);
        self.renderSummary();
        self.advanceOrFinish();
      })
      .catch(function (e) {
        self.setBusy(false);
        console.error('[addon-flow] add to cart failed:', e);
      });
  };

  AddonFlow.prototype.advanceOrFinish = function () {
    if (this.current >= this.steps.length - 1) {
      this.goToCheckout();
      return;
    }
    this.goTo(this.current + 1);
  };

  // Are there essential steps the customer hasn't proceeded through yet?
  AddonFlow.prototype.hasUncommittedEssential = function () {
    var self = this;
    return this.steps.some(function (step, i) {
      return step.getAttribute('data-essential') === 'true' && !self.committed[i];
    });
  };

  // "Skip to Checkout": warn if essentials are still pending, else check out.
  AddonFlow.prototype.skipToCheckout = function () {
    if (this.hasUncommittedEssential() && this.skipModal && typeof this.skipModal.showModal === 'function') {
      this.skipModal.showModal();
    } else {
      this.goToCheckout();
    }
  };

  // Seed the machine if needed, then navigate to checkout.
  AddonFlow.prototype.goToCheckout = function () {
    var checkout = CART_ROOT + 'checkout';
    // Phase 4: flow handed off to checkout — clear the in-progress flag. Fire-
    // and-forget; if the write must be guaranteed it's better cleared on the
    // order/thank-you pixel, since the page unloads on navigation.
    // TODO(convert): decide clear-on-checkout vs. clear-on-purchase.
    setFlowInProgress(false);
    var items = this.machineSeedItems();
    if (!items.length) {
      window.location.href = checkout;
      return;
    }
    this.setBusy(true);
    cartAdd(items)
      .then(function () { window.location.href = checkout; })
      .catch(function () { window.location.href = checkout; });
  };

  AddonFlow.prototype.setBusy = function (busy) {
    this.busy = busy;
    if (this.continueBtn) this.continueBtn.disabled = busy;
    if (this.skipBtn) this.skipBtn.disabled = busy;
    if (this.checkoutLink) this.checkoutLink.disabled = busy;
    this.root.classList.toggle('is-busy', busy);
  };

  /* ── Cards ───────────────────────────────────────────────────────────── */

  AddonFlow.prototype.bindCard = function (card) {
    var self = this;
    card.variants = parseJSON(card.querySelector('[data-addon-card-variants]'), null);

    var qtyInput = card.querySelector('[data-addon-qty-input]');
    var decrease = card.querySelector('[data-addon-qty-decrease]');
    var increase = card.querySelector('[data-addon-qty-increase]');

    // Selection state lives on the card's data-quantity; the summary only
    // reflects the real cart, so selecting doesn't re-render it (QA request).
    function setQty(next) {
      var qty = Math.max(0, parseInt(next, 10) || 0);
      card.setAttribute('data-quantity', qty);
      if (qtyInput) qtyInput.value = qty;
      card.classList.toggle('is-selected', qty > 0);
    }

    if (decrease) decrease.addEventListener('click', function () {
      setQty(parseInt(card.getAttribute('data-quantity'), 10) - 1);
    });
    if (increase) increase.addEventListener('click', function () {
      setQty(parseInt(card.getAttribute('data-quantity'), 10) + 1);
    });
    if (qtyInput) qtyInput.addEventListener('change', function () {
      setQty(qtyInput.value);
    });

    // Checkbox mode (e.g. Support step): checked = qty 1, unchecked = qty 0.
    var check = card.querySelector('[data-addon-check]');
    if (check) {
      check.addEventListener('change', function () {
        setQty(check.checked ? 1 : 0);
      });
    }

    var selects = Array.prototype.slice.call(card.querySelectorAll('[data-addon-option]'));
    if (selects.length && card.variants) {
      selects.forEach(function (select) {
        select.addEventListener('change', function () {
          self.resolveVariant(card, selects);
        });
      });
    }

    // Frequency change → update the displayed subscribe price for this plan.
    var planSelect = card.querySelector('[data-addon-plan-select]');
    if (planSelect) {
      planSelect.addEventListener('change', function () {
        self.refreshSubPrice(card);
      });
    }
  };

  // The variant object (from the embedded variants JSON) for the card's current
  // data-variant-id.
  AddonFlow.prototype.currentVariantData = function (card) {
    if (!card.variants) return null;
    var vid = card.getAttribute('data-variant-id');
    for (var i = 0; i < card.variants.length; i++) {
      if (String(card.variants[i].id) === String(vid)) return card.variants[i];
    }
    return null;
  };

  // Re-render the frequency <select> to the current variant's selling plans so we
  // never offer a plan the variant can't take (avoids the 422 on /cart/add.js).
  AddonFlow.prototype.refreshPlans = function (card) {
    var sel = card.querySelector('[data-addon-plan-select]');
    if (!sel) return;
    var vdata = this.currentVariantData(card);
    var plans = (vdata && vdata.selling_plans) || [];
    var previous = sel.value;
    sel.innerHTML = '';
    plans.forEach(function (p) {
      var opt = document.createElement('option');
      opt.value = p.id;
      opt.textContent = p.name;
      sel.appendChild(opt);
    });
    // Preserve the chosen frequency if the new variant still offers it.
    if (plans.some(function (p) { return String(p.id) === previous; })) sel.value = previous;
    this.refreshSubPrice(card);
  };

  // Update the "Subscribe & Save" price to the currently selected plan.
  AddonFlow.prototype.refreshSubPrice = function (card) {
    var sel = card.querySelector('[data-addon-plan-select]');
    var priceEl = card.querySelector('.addon-flow__plan-box--sub .addon-flow__plan-price');
    if (!sel || !priceEl) return;
    var vdata = this.currentVariantData(card);
    var plans = (vdata && vdata.selling_plans) || [];
    if (!plans.length) return;
    var chosen = null;
    for (var i = 0; i < plans.length; i++) {
      if (String(plans[i].id) === sel.value) { chosen = plans[i]; break; }
    }
    if (!chosen) chosen = plans[0];
    priceEl.textContent = formatMoney(chosen.price);
  };

  // Resolve the selected selling plan for a card: subscribe → the frequency
  // <select>'s plan id + label; one-time → empty.
  AddonFlow.prototype.getCardPlan = function (card) {
    var purchase = card.querySelector('[data-addon-plans] [data-addon-purchase]:checked');
    if (!purchase || purchase.value !== 'subscribe') return { id: '', name: '' };
    var sel = card.querySelector('[data-addon-plan-select]');
    if (!sel || !sel.value) return { id: '', name: '' };
    var opt = sel.options[sel.selectedIndex];
    return { id: sel.value, name: opt ? opt.text.trim() : '' };
  };

  AddonFlow.prototype.resolveVariant = function (card, selects) {
    var chosen = selects
      .slice()
      .sort(function (a, b) {
        return (parseInt(a.getAttribute('data-addon-option'), 10) || 0) -
          (parseInt(b.getAttribute('data-addon-option'), 10) || 0);
      })
      .map(function (s) { return s.value; });

    var match = null;
    for (var i = 0; i < card.variants.length; i++) {
      var opts = card.variants[i].options || [];
      if (opts.length === chosen.length && opts.every(function (o, idx) { return o === chosen[idx]; })) {
        match = card.variants[i];
        break;
      }
    }
    if (!match) return;

    card.setAttribute('data-variant-id', match.id);
    card.setAttribute('data-price', match.price);
    var priceEl = card.querySelector('.addon-flow__price-current');
    if (priceEl) priceEl.textContent = formatMoney(match.price);

    // The new variant may support different selling plans — re-render them.
    this.refreshPlans(card);
  };

  // Items selected on a given step, formatted for /cart/add.js.
  AddonFlow.prototype.collectStepItems = function (stepIndex) {
    var self = this;
    var step = this.steps[stepIndex];
    if (!step) return [];
    return Array.prototype.slice.call(step.querySelectorAll('[data-addon-card]'))
      .map(function (card) {
        var qty = parseInt(card.getAttribute('data-quantity'), 10) || 0;
        if (qty <= 0) return null;
        var item = { id: parseInt(card.getAttribute('data-variant-id'), 10), quantity: qty };
        var plan = self.getCardPlan(card);
        if (plan.id) item.selling_plan = parseInt(plan.id, 10);
        return item;
      })
      .filter(Boolean);
  };

  /* ── Summary (real cart only — selections appear after "Add and Continue") ─ */

  AddonFlow.prototype.renderSummary = function () {
    if (!this.itemsEl) return;

    this.itemsEl.innerHTML = '';
    var frag = document.createDocumentFragment();

    // Only the real cart (machine + already-committed add-ons) is shown. The
    // current step's selections are NOT previewed here — they appear once the
    // customer commits them via "Add and Continue" (QA request).
    (this.cart.items || []).forEach(function (item) {
      frag.appendChild(buildLine({
        title: item.product_title || item.title,
        image: item.image,
        variantTitle: (item.variant_title && item.variant_title.indexOf('Default') === -1) ? item.variant_title : '',
        planName: (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan)
          ? item.selling_plan_allocation.selling_plan.name : '',
        quantity: item.quantity,
        linePrice: item.final_line_price
      }, 'cart'));
    });

    this.itemsEl.appendChild(frag);

    var total = this.cart.total_price || 0;
    var count = this.cart.item_count || 0;
    if (this.subtotalEl) this.subtotalEl.textContent = formatMoney(total);
    if (this.countEl) this.countEl.textContent = count;
    if (this.countNounEl) this.countNounEl.textContent = count === 1 ? 'item' : 'items';
  };

  function buildLine(data, kind) {
    var li = document.createElement('li');
    li.className = 'addon-flow__summary-line';
    li.setAttribute('data-addon-summary-line', kind);

    var media = document.createElement('div');
    media.className = 'addon-flow__summary-media';
    if (data.image) {
      var img = document.createElement('img');
      img.className = 'addon-flow__summary-image';
      img.src = data.image;
      img.alt = data.title || '';
      img.width = 60;
      img.height = 60;
      img.loading = 'lazy';
      media.appendChild(img);
    }
    var qty = document.createElement('span');
    qty.className = 'addon-flow__summary-qty';
    qty.setAttribute('aria-hidden', 'true');
    qty.textContent = data.quantity;
    media.appendChild(qty);

    var detail = document.createElement('div');
    detail.className = 'addon-flow__summary-detail';
    var title = document.createElement('p');
    title.className = 'addon-flow__summary-title';
    title.textContent = data.title;
    detail.appendChild(title);

    if (data.variantTitle) {
      var v = document.createElement('p');
      v.className = 'addon-flow__summary-variant';
      v.textContent = data.variantTitle;
      detail.appendChild(v);
    }
    if (data.planName) {
      var p = document.createElement('p');
      p.className = 'addon-flow__summary-plan';
      p.textContent = data.planName;
      detail.appendChild(p);
    }

    var price = document.createElement('p');
    price.className = 'addon-flow__summary-price';
    price.textContent = formatMoney(data.linePrice);

    li.appendChild(media);
    li.appendChild(detail);
    li.appendChild(price);
    return li;
  }

  /* ── Boot ────────────────────────────────────────────────────────────── */

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll(ROOT_SELECTOR);
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.addonFlowInit) return;
      root.dataset.addonFlowInit = 'true';
      new AddonFlow(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
